<template>
    <div
        id="bpmn"
        :class="{
            'disabled': props.isDisabled,
            'editing': isEditing,
            'navigatable': props.isNavigationEnabled,
        }"
        v-bind="$attrs"
    >
        <div v-if="props.isDisabled" id="disabled-overlay" />
        <div id="diagram-container" ref="bpmnJsElement"></div>
        <div class="floating" v-if="props.isMaximizingEnabled">
            <Button
                v-if="organization && stek && process"
                severity="secondary"
                class="maximize-button"
                icon="pi pi-arrow-up-right-and-arrow-down-left-from-center"
                label="Maximize"
                size="small"
                :as="isLiveMode ? 'button' : 'RouterLink'"
                :to="
                    isLiveMode
                        ? undefined
                        : {
                              name: 'processDefinitionPage',
                              params: {
                                  orgName: organization.name,
                                  stekName: stek.name,
                                  processId: process.identity,
                              },
                          }
                "
                @click="isLiveMode ? (showMaximizedDialog = true) : undefined"
            />
        </div>
    </div>

    <!-- Maximized dialog for LIVE mode -->
    <Dialog
        v-model:visible="showMaximizedDialog"
        modal
        :style="{ width: '95vw', height: '95vh' }"
        :closable="true"
        :draggable="false"
        :resizable="false"
        class="bpmn-maximized-dialog"
        @show="onMaximizedDialogShow"
        @hide="onMaximizedDialogHide"
    >
        <template #header>
            <h2>
                <i class="pi pi-share-alt"></i>
                Process Definition
            </h2>
        </template>
        <div class="maximized-bpmn-container">
            <div
                id="bpmn-maximized"
                :class="{
                    'disabled': false,
                    'navigatable': props.isNavigationEnabled,
                }"
            >
                <div
                    id="diagram-container-maximized"
                    ref="bpmnJsElementMaximized"
                ></div>
            </div>
        </div>
    </Dialog>
</template>

<script lang="ts" setup>
import { getBusinessObject, is } from "bpmn-js/lib/util/ModelUtil";
import Button from "primevue/button";
import Dialog from "primevue/dialog";
import { type ToastMessageOptions } from "primevue/toast";
import { useToast } from "primevue/usetoast";
import {
    computed,
    nextTick,
    onMounted,
    onUnmounted,
    readonly,
    ref,
    watch,
} from "vue";
import { onBeforeRouteLeave, useRoute, useRouter } from "vue-router";

import {
    elementIsEvent,
    elementIsGateway,
    elementIsMessageFlow,
    elementIsTask,
    elementType,
} from "@/composables/bpmn-manager/helpers";
import {
    ImportBPMNDiagramError,
    ImportWarning,
    useBpmnManager,
} from "@/composables/bpmn-manager/index";
import { useBpmnLayerManager } from "@/composables/bpmn-manager/layer";
import { useEditManager } from "@/composables/edit-manager";
import { useOrganizationManager } from "@/composables/organization-manager";
import { useProcessManager } from "@/composables/process-manager";
import { useStekManager } from "@/composables/stek-manager";
import { BpmnArtefact } from "@/lib/artefacts";
import { bpmnToImage } from "@/lib/bpmn-helper";
import { useProcessElementsManager } from "@/stores/process-elements-store";
import { debounce } from "@/utils";

const { organization } = useOrganizationManager();
const { stek } = useStekManager();
const { process } = useProcessManager();

const bpmnArtefact = defineModel<BpmnArtefact>("bpmnArtefact", {
    required: true,
});

const highlightElementId = defineModel<string | null>("highlightElementId", {
    default: "",
});

const props = withDefaults(
    defineProps<{
        isDisabled?: boolean;
        canEdit?: boolean;
        isMaximizingEnabled?: boolean;
        isNavigationEnabled?: boolean;
        isScrollingEnabled?: boolean;
        isHoveringEnabled?: boolean;
        isZoomToHighlightEnabled?: boolean;
    }>(),
    {
        isDisabled: false,
        canEdit: true,
        isMaximizingEnabled: true,
        isNavigationEnabled: true,
        isScrollingEnabled: true,
        isHoveringEnabled: true,
        isZoomToHighlightEnabled: false,
    }
);

const route = useRoute();
const router = useRouter();
const toast = useToast();

// Check if we're in LIVE mode
const isLiveMode = computed(() => {
    return route.matched.some((m) => m.meta.mode === "live");
});
const showMaximizedDialog = ref(false);
const bpmnJsElement = ref<HTMLElement>();
const bpmnJsElementMaximized = ref<HTMLElement>();
const bpmnManager = useBpmnManager();
const bpmnManagerMaximized = useBpmnManager();

defineExpose({
    getImage: (type: "png" | "svg" | "jpeg") =>
        bpmnToImage(bpmnManager.bpmnJs.value, type),
    completeActiveActions: bpmnManager.completeActiveActions,
});

const { isEditing: isGlobalEditing } = useEditManager();
const isEditing = computed(() => isGlobalEditing.value && props.canEdit);
const roleSelect = true;
const layerManager = useBpmnLayerManager(bpmnManager, {
    highlight: {
        enabled: readonly(
            ref(props.isHoveringEnabled ? "onAll" : "onExternal")
        ),
        highlightSeletor: (element) => {
            const bo = getBusinessObject(element);
            const isStandardNavigational =
                (elementIsTask(element) ||
                    elementIsEvent(element) ||
                    elementIsMessageFlow(element) ||
                    elementIsGateway(element)) &&
                bo.processElementId;

            const isLinkedEntity =
                (is(element, "bpmn:CallActivity") ||
                    (is(element, "bpmn:Participant") && element.collapsed)) &&
                (bo.processId || bo.calledElementId);

            return isStandardNavigational || isLinkedEntity;
        },
    },
    dotForMissingTasks: {
        enabled: readonly(ref(false)),
    },
    navigationalElements: {
        enabled: computed(() => !isEditing.value),
        callbacks: props.isHoveringEnabled
            ? {
                  onHoveringNavigationalElement: (elementId) => {
                      highlightElementId.value = elementId;
                  },
              }
            : undefined,
    },
    taskWindowElements: {
        enabled: computed(() => isEditing.value),
    },
    roleSelect: {
        enabled: readonly(ref(roleSelect)),
    },
    hoveringPopup: {
        enabled: readonly(ref(props.isHoveringEnabled)),
    },
});

// Keep track of when the highlight was last applied
const lastHighlightedElementId = ref<string | null>(null);

watch(highlightElementId, (elementId, oldElementId) => {
    if (oldElementId) {
        layerManager.clearHighlight(oldElementId);
    }

    if (elementId && bpmnManager.initialized.value) {
        // Remember this element ID
        lastHighlightedElementId.value = elementId;

        // First highlight the element
        layerManager.highlight(elementId);

        if (props.isZoomToHighlightEnabled) {
            // Then zoom to it
            setTimeout(() => {
                // Only apply if this is still the current element ID
                if (lastHighlightedElementId.value === elementId) {
                    bpmnManager.zoomToElementId(elementId, 1);
                }
            }, 50);
        }
    }
});

const { processElements } = useProcessElementsManager();
watch(bpmnManager.selectedBpmnElement, (element) => {
    if (!isEditing.value) {
        if (!element) {
            return;
        }

        let processElementId = getBusinessObject(element).processElementId;
        if (!processElementId) {
            const processElement = processElements.value?.find(
                (task) => element.businessObject?.id === task.bpmn_id
            );
            processElementId = processElement?.identity;
        }

        if (processElementId) {
            // Only highlight if we actually will/can navigate to it.
            highlightElementId.value = element.businessObject?.id;

            // Don't navigate in LIVE mode - users must switch to DESIGN mode manually
            if (!isLiveMode.value) {
                router.push({
                    name: "processElementPage",
                    params: {
                        ...route.params,
                        elementId: processElementId,
                        elementType: elementType(element),
                    },
                });
            }
        }
    }
});

const debounceCenter = debounce(() => {
    // Don't reset zoom if we have a highlighted element
    if (highlightElementId.value && props.isZoomToHighlightEnabled) {
        bpmnManager.zoomToElementId(highlightElementId.value, 1);
    } else {
        bpmnManager.resetZoom();
    }
}, 100);

let resizeObserver: ResizeObserver;
onMounted(() => {
    initBpmnJs();
    resizeObserver = new ResizeObserver(debounceCenter);
    resizeObserver.observe(bpmnJsElement.value!);
});

onUnmounted(() => {
    bpmnManager.destroy();
    bpmnManagerMaximized.destroy();
    resizeObserver?.disconnect();
});

// Initialize maximized BPMN viewer when dialog opens
const onMaximizedDialogShow = async () => {
    if (!bpmnArtefact.value?.content) {
        console.error("BPMN artefact content not available");
        return;
    }

    // Wait for dialog to be fully rendered
    await nextTick();
    await nextTick();

    // Additional delay to ensure DOM is ready
    setTimeout(async () => {
        if (!bpmnJsElementMaximized.value) {
            console.error("Maximized BPMN element not found");
            return;
        }

        try {
            bpmnManagerMaximized.initViewer(bpmnJsElementMaximized.value, {
                isNavigationEnabled: props.isNavigationEnabled,
                isScrollingEnabled: true,
                hideLaneNames: roleSelect,
            });

            const { warnings } = await bpmnManagerMaximized.loadBpmn(
                bpmnArtefact.value.content
            );
            if (warnings?.length) {
                showWarningToast(warnings);
            }
        } catch (e) {
            console.error("Failed to initialize maximized BPMN viewer:", e);
            showErrorToast(e as ImportBPMNDiagramError);
        }
    }, 100);
};

const onMaximizedDialogHide = () => {
    // Clean up when dialog closes
    bpmnManagerMaximized.destroy();
};

// Adding the toast secretly adds an id value to it, we need that to remove it.
const errorMessageToast = ref<ToastMessageOptions & { id?: number }>();

/**
 * Extract the message from an ImportWarning object.
 */
const messageFromWarning = (warning: ImportWarning) => {
    if (Object.hasOwn(warning, "message")) {
        return (warning as ImportWarning).message;
    }
    return warning;
};

/**
 * Show a toast with an error message if the BPMN is invalid.
 * Remove an existing toast for any prior BPMN errors.
 */
const showErrorToast = (error: ImportBPMNDiagramError) => {
    if (errorMessageToast.value?.id !== undefined) {
        toast.remove(errorMessageToast.value);
    }
    errorMessageToast.value = {
        severity: "error",
        summary: "An invalid BPMN was loaded.",
        detail:
            error.warnings?.map(messageFromWarning).join(",") || error.message,
    };
    toast.add(errorMessageToast.value);
};

/**
 * Initialize the bpmn-js component, either as editor or as viewer
 */
const initBpmnJs = async () => {
    let viewbox = bpmnManager.getViewbox() ?? null;
    if (!viewbox) {
        try {
            viewbox = JSON.parse(
                sessionStorage.getItem(
                    `${bpmnArtefact.value.identity}-viewbox`
                ) ?? "undefined"
            );
            // Make sure it's not hanging around for when you get back later.
            sessionStorage.removeItem(`${bpmnArtefact.value.identity}-viewbox`);
        } catch {
            // no or invalid value, leave as is.
        }
    }
    const rootElement = bpmnManager.getRootElement();
    let rootElementId: string | undefined | null;
    if (rootElement) {
        rootElementId = rootElement.id;
    } else {
        rootElementId = sessionStorage.getItem(
            `${bpmnArtefact.value.identity}-rootElementId`
        );
        // Make sure it's not hanging around for when you get back later.
        sessionStorage.removeItem(
            `${bpmnArtefact.value.identity}-rootElementId`
        );
    }

    if (isEditing.value && !props.isDisabled) {
        bpmnManager.initModeler(bpmnJsElement.value!, undefined, {
            hideLaneNames: roleSelect,
        });
    } else {
        bpmnManager.initViewer(bpmnJsElement.value!, {
            isNavigationEnabled: props.isNavigationEnabled,
            isScrollingEnabled: props.isScrollingEnabled,
            hideLaneNames: roleSelect,
        });
    }

    layerManager.attach();

    try {
        const { warnings } = await bpmnManager.loadBpmn(
            bpmnArtefact.value.content
        );
        if (warnings?.length) {
            console.warn(warnings);
        }
    } catch (e) {
        showErrorToast(e as ImportBPMNDiagramError);
        bpmnArtefact.value.resetContent();
        return;
    }
    if (viewbox && (props.isNavigationEnabled || isEditing.value)) {
        bpmnManager.setViewbox(viewbox);
    } else {
        bpmnManager.resetZoom();
    }
    if (rootElementId) {
        bpmnManager.setRootElement(rootElementId);
    }

    // After BPMN is fully loaded, apply highlighting and zooming if we have an element ID
    if (highlightElementId.value && props.isZoomToHighlightEnabled) {
        // Layermanager updates are debounced, so make sure it's triggered after the first update.
        setTimeout(() => {
            bpmnManager.zoomToElementId(highlightElementId.value!);
            layerManager.highlight(highlightElementId.value!);
        }, 100);
    }
};

onBeforeRouteLeave(() => {
    const viewbox = bpmnManager.getViewbox();
    if (viewbox) {
        sessionStorage.setItem(
            `${bpmnArtefact.value.identity}-viewbox`,
            JSON.stringify(viewbox)
        );
    }
    const rootElement = bpmnManager.getRootElement();
    if (rootElement && rootElement.id) {
        sessionStorage.setItem(
            `${bpmnArtefact.value.identity}-rootElementId`,
            rootElement.id
        );
    }
});

watch(() => isEditing.value, initBpmnJs);

// Sometimes the artefact is changed from the outside (e.g. when the user
// discards their changes) - in that case we reload the bpmn.
watch(
    () => bpmnArtefact.value.content,
    async (newContent, oldContent) => {
        if (newContent && newContent !== bpmnManager.xml.value) {
            try {
                const { warnings } = await bpmnManager.loadBpmn(newContent);
                if (warnings?.length) {
                    console.warn(warnings);
                }

                // After BPMN is reloaded, apply highlighting and zooming if we have an element ID
                if (
                    highlightElementId.value &&
                    props.isZoomToHighlightEnabled
                ) {
                    setTimeout(() => {
                        bpmnManager.zoomToElementId(highlightElementId.value!);
                        layerManager.highlight(highlightElementId.value!);
                    }, 100);
                }
            } catch (e) {
                showErrorToast(e as ImportBPMNDiagramError);
                bpmnArtefact.value.content = oldContent;
                return;
            }
        }
    }
);

watch(bpmnManager.xml, async (newValue) => {
    if (
        isEditing.value &&
        newValue &&
        newValue !== bpmnArtefact.value.content
    ) {
        const previewImage = await bpmnToImage(bpmnManager.bpmnJs.value, "svg");
        if (previewImage) {
            const previewEncoded: string = await new Promise((resolve, _) => {
                const reader = new FileReader();
                reader.onloadend = () => {
                    resolve(reader.result);
                };
                reader.readAsDataURL(previewImage);
            });
            bpmnArtefact.value.preview = previewEncoded;
        }
        bpmnArtefact.value.updateContent(newValue);
    }
});
</script>

<style lang="scss" scoped>
@keyframes pulse {
    from {
        transform: scale3d(1, 1, 1);
        opacity: 0.75;
    }
    50% {
        transform: scale3d(2, 2, 1);
        opacity: 0;
    }
    to {
        opacity: 0;
    }
}

.floating {
    position: absolute;
    right: 1rem;
    top: 1rem;
}

.maximize-button {
    text-decoration: none;
}

.maximized-bpmn-container {
    height: calc(95vh - 120px);
    width: 100%;
    overflow: hidden;
    position: relative;
}

#bpmn-maximized {
    display: flex;
    flex-grow: 1;
    height: 100%;
    width: 100%;
    position: relative;
}

#diagram-container-maximized {
    flex: 1;
    height: 100%;
    width: 100%;
}

:deep(.bpmn-maximized-dialog .p-dialog-content) {
    padding: 0;
    overflow: hidden;
    height: calc(95vh - 120px);
}

:deep(.bpmn-maximized-dialog .p-dialog-header) {
    padding: 1rem;
    border-bottom: 1px solid var(--surface-border);
}

:deep(.bpmn-maximized-dialog .p-dialog-header h2) {
    margin: 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

#bpmn {
    display: flex;
    flex-grow: 1;
    height: 100%;
    position: relative;

    #diagram-container {
        flex: 1;

        :deep(.djs-container.djs-parent) svg {
            outline: none;
        }
    }

    :deep(.simulation) {
        .bjsl-button {
            display: none;
        }
    }

    /* Avoid showing the 'move' cursor when things are not movable */
    &:not(.navigatable):not(.editing) {
        :deep(.djs-element) {
            .djs-hit-all,
            .djs-hit-stroke,
            .djs-hit-click-stroke,
            .djs-connection {
                cursor: unset;
            }
        }
    }

    &.editing {
        :deep(.djs-element) {
            &.hover {
                .djs-outline {
                    stroke: var(--stekz-biz-lightest-color);
                }
            }
            &.selected {
                .djs-outline {
                    stroke: var(--stekz-biz-darkest-color);
                    stroke-width: 2px;
                }
                /* Selected task (rect), gateway (polygon) or start/end/error (circle) */
                .djs-visual {
                    rect,
                    circle,
                    polygon {
                        fill: var(--stekz-biz-lightest-color) !important;
                    }
                }
            }
        }
    }

    &:not(.editing) {
        :deep(.djs-element) {
            &.hover {
                .djs-outline {
                    stroke: unset;
                }
            }
            &.selected {
                .djs-outline {
                    stroke: unset;
                }
            }
            .djs-hit-stroke,
            .djs-hit-click-stroke,
            .djs-hit-all {
                cursor: unset;
            }
        }
    }

    :deep(.djs-element) {
        .djs-call-activity .djs-label,
        .djs-call-activity .djs-label ~ *, // The icon rect and plus sign
        .djs-collapsed-pool .djs-label {
            fill: var(--stekz-primary-color) !important;
            text-align: center;
            vertical-align: middle;
        }
    }

    :deep(.djs-overlay-linting) {
        .bjsl-icon:after {
            content: " ";
            position: absolute;
            display: block;
            inset: 0;
            z-index: -1;
            background-color: var(--icon-bg-color);
            border-radius: 100%;
            animation: pulse 1.5s infinite;
        }
    }

    &.disabled {
        #disabled-overlay {
            position: absolute;
            top: 0;
            bottom: 0;
            left: 0;
            right: 0;
            background: var(--p-content-background);
            opacity: 0.9;
            z-index: 1;
        }
    }
}
</style>
<style lang="css">
@import url("bpmn-js/dist/assets/diagram-js.css");
@import url("bpmn-js/dist/assets/bpmn-js.css");
@import url("bpmn-js/dist/assets/bpmn-font/css/bpmn.css");
@import url("bpmn-js/dist/assets/bpmn-font/css/bpmn-embedded.css");
@import url("bpmn-js-bpmnlint/dist/assets/css/bpmn-js-bpmnlint.css");
@import url("@bpmn-io/properties-panel/assets/properties-panel.css");
@import url("bpmn-js-token-simulation/assets/css/bpmn-js-token-simulation.css");
@import url("bpmn-js-color-picker/colors/color-picker.css");
/* Overwrite token-simulation colors */
:root {
    --token-simulation-green-base-44: var(--stekz-primary-color);
}
</style>
